import { defineStore } from "pinia";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import type { Paged, Pagination } from "@/types";
import {
  FilterContext,
  type GlobalWorkspacesFilters,
  type MspDevicesFilters,
  type MspFilters,
  type MspTicketsFilters,
  type MspUsersFilters,
  useFiltersStore,
} from "@/_store/filters.module";
import { StoreModule } from "@/constants/store.ts";
import { ExportChoice, type ExportListItem } from "@/constants/exports.ts";
import { type ExportDialogPayload } from "@/_store/exports.module.ts";
import { convertMspTicketFiltersForBackend } from "@/_store/msp/tickets/adapters.ts";
import moment from "moment/moment";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import { getMspFilters } from "@/_store/msp/msp.module.ts";
import type { AxiosRequestConfig } from "axios";
import type { TimeFrame } from "@/types";
import { convertTimeFrameToUTCUnix } from "@/_helpers/date";
import { useSnackbarStore } from "@/_store/snackbar.module";
import { i18n } from "@/plugins/i18n";
import { MSP_WARNING_EMAIL_EVENTS_WORKSPACE_LIMIT } from "@/constants/reports";

interface MspExportsStoreState {
  loading: boolean;
  exportsList: Paged<ExportListItem>;
  pagination: Pagination;
}

const defaultReportsState: MspExportsStoreState = {
  loading: false,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  exportsList: {
    items: [],
    total: 0,
  },
};

export const useMspExportsStore = defineStore(StoreModule.MSP_EXPORTS, {
  state: (): MspExportsStoreState => ({ ...defaultReportsState }),
  actions: {
    async getExportsList() {
      const { search, types } = useFiltersStore().filters[FilterContext.MSP_EXPORTS_FILTERS];
      const request = {
        ...api.mspExports(),
        params: {
          search,
          types: (types ?? []).join(","),
          ...this.pagination,
        },
      };
      try {
        this.loading = true;
        const { data } = await axiosInstance.request(request);
        this.exportsList = data;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
    async exportUsersToCsv({ type, filters }: ExportDialogPayload<MspUsersFilters>) {
      const { cloudServices, workspaceIds } = filters;
      const filtersPayload =
        type === ExportChoice.ALL
          ? { hasVulnerabilities: null }
          : {
              hasVulnerabilities: null,
              cloudServices: cloudServices ?? [],
              workspaceIds: workspaceIds ?? [],
            };
      const request = {
        ...api.exportMspUsersToCsv(),
        data: { ...filtersPayload },
      };
      try {
        await axiosInstance.request(request);
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    async exportDevicesToCsv({ type, filters }: ExportDialogPayload<MspDevicesFilters>) {
      const { childWorkspaceIds, osVersion, clientVersion, vulnerabilities, statuses, labels } =
        filters;

      const filtersPayload =
        type === ExportChoice.ALL
          ? { hasVulnerabilities: null }
          : {
              hasVulnerabilities: null,
              vulnerabilities,
              osVersion,
              deviceStatuses: statuses,
              clientVersion,
              labels,
              childWorkspaceIds: childWorkspaceIds ?? [],
            };

      const request = {
        ...api.exportMspDevicesToCsv(),
        data: {
          filter: filtersPayload,
        },
      };
      try {
        await axiosInstance.request(request);
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    async exportTicketsToCsv({ type, filters }: ExportDialogPayload<MspTicketsFilters>) {
      const request = {
        ...api.exportMspTicketsToCsv(),
        data: {
          ...buildTicketsFiltersPayload(type, filters),
        },
      };
      try {
        await axiosInstance.request(request);
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    async exportTicketsSummaryToCsv({ type, filters }: ExportDialogPayload<MspTicketsFilters>) {
      const request = {
        ...api.exportMspTicketsSummaryToCsv(),
        data: {
          ...buildTicketsFiltersPayload(type, filters),
        },
      };
      try {
        await axiosInstance.request(request);
      } catch (err) {
        console.error(err);
        throw err;
      }
    },
    async exportWorkspacesList(
      { type, filters }: ExportDialogPayload<MspFilters | GlobalWorkspacesFilters>,
      context: ManageWorkspacesContext = ManageWorkspacesContext.SINGLE_HIERARCHY
    ) {
      const request = buildWorkspaceExportRequest({ type, filters }, context, {
        msp: api.exportMspWorkspaces,
        global: api.exportGlobalWorkspaces,
      });
      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async exportAdminNotifications(
      { type, filters }: ExportDialogPayload<MspFilters | GlobalWorkspacesFilters>,
      context: ManageWorkspacesContext = ManageWorkspacesContext.SINGLE_HIERARCHY
    ) {
      const request = buildWorkspaceExportRequest({ type, filters }, context, {
        msp: api.exportMspNotifications,
        global: api.exportGlobalNotifications,
      });
      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async exportWorkspacesSummary(
      { type, filters }: ExportDialogPayload<MspFilters | GlobalWorkspacesFilters>,
      context: ManageWorkspacesContext = ManageWorkspacesContext.SINGLE_HIERARCHY
    ) {
      const request = buildWorkspaceExportRequest(
        { type, filters },
        context,
        { msp: api.exportMspSummary, global: api.exportGlobalMspSummary },
        {
          params: {
            type: "csv",
            resolvedFromTime: moment().subtract(90, "days").startOf("day").utc().valueOf(),
          },
        }
      );
      try {
        await axiosInstance.request(request);
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    async exportMspWarningEmailEventsReportToCsv(payload: TimeFrame) {
      const convertedTimeFrame = convertTimeFrameToUTCUnix(payload);
      const request = {
        ...api.exportMspWarningEmailEventsToCsv,
        data: {
          ...convertedTimeFrame,
          workspaceLimit: MSP_WARNING_EMAIL_EVENTS_WORKSPACE_LIMIT,
        },
      };
      try {
        await axiosInstance.request(request);
        useSnackbarStore().addGenericSuccess(
          i18n.global.t("snackbar.messages.exports.exportCsvStarted")
        );
      } catch (err) {
        console.error(err);
      }
    },
  },
});

function buildTicketsFiltersPayload(
  type: ExportChoice,
  filters: MspTicketsFilters
): { filters: Record<string, unknown> } {
  if (type === ExportChoice.ALL) {
    return { filters: { directDescendantsOnly: false } };
  }

  const { eventTriggers, widget, childWorkspaceIds } = filters;
  const { fromTime, toTime } = convertMspTicketFiltersForBackend(filters);

  return {
    filters: {
      fromTime,
      toTime,
      processed: null,
      eventTriggers,
      widget,
      childWorkspaceIds: childWorkspaceIds ?? [],
    },
  };
}

function buildWorkspaceExportRequest<T extends MspFilters | GlobalWorkspacesFilters>(
  payload: ExportDialogPayload<T>,
  context: ManageWorkspacesContext,
  apiFactory: { msp: () => AxiosRequestConfig; global: () => AxiosRequestConfig },
  overrides: Partial<AxiosRequestConfig> = {}
): AxiosRequestConfig {
  const { type, filters } = payload;
  const filtersPayload = type === ExportChoice.ALL ? {} : getMspFilters(filters);
  const baseRequest =
    context === ManageWorkspacesContext.GLOBAL_WORKSPACES ? apiFactory.global() : apiFactory.msp();

  const overrideData = (overrides.data as Record<string, unknown> | undefined) ?? {};

  return {
    ...baseRequest,
    ...overrides,
    data: {
      ...filtersPayload,
      ...overrideData,
    },
  };
}
